import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Card, Icon, Section } from "@/components/primitives";

// What's new — hand-curated changelog surfaced from Settings → Tips.
// New entries go at the top. Keep entries short and user-facing —
// "we added 90 brands" matters more than "refactored brandTriage.ts".

interface Entry {
  date: string;          // ISO date for sort; display is humanized in render
  title: string;
  body: string;
  tag?: "feature" | "accuracy" | "brands" | "design" | "fix";
}

const ENTRIES: Entry[] = [
  {
    date: "2026-05-15",
    title: "Profile redesign",
    body: "New postcard layout, single page handles both your own and visitor views. Shops are now front-and-center; badges sit just below. Edit affordances moved to Settings.",
    tag: "design",
  },
  {
    date: "2026-05-15",
    title: "90+ new brands added to triage",
    body: "Coverage now spans Edwardian and pre-war French couture (Vionnet, Schiaparelli, Patou, Worth) through 60s mod (Quant, Biba, Ossie Clark), 50s-60s American (Norell, Galanos, Carnegie), 40s American Look pioneers, vintage costume jewelry (Eisenberg, Trifari, Miriam Haskell), and more.",
    tag: "brands",
  },
  {
    date: "2026-05-15",
    title: "Premier department-store provenance boost",
    body: "Pieces double-labeled with Bergdorf Goodman, Henri Bendel, Bonwit Teller, Bullocks Wilshire, I. Magnin, and other premier US stores now get a tier boost — recognizes the curation signal that FIG was missing before.",
    tag: "accuracy",
  },
  {
    date: "2026-05-15",
    title: "Hand-verification advisory",
    body: "High-value pieces ($1000+), pre-1975 designer items, and heavily counterfeited brands now surface a soft 'verify in hand before listing' note alongside the price band.",
    tag: "feature",
  },
  {
    date: "2026-05-15",
    title: "Closet item statuses go live",
    body: "Items now correctly flip from KEPT to LISTED when you add marketplace links, and the LISTED badge shows the marketplace name (EBAY, DEPOP, ETSY) as a clickable link to your shop.",
    tag: "fix",
  },
  {
    date: "2026-05-15",
    title: "Mood-board feed",
    body: "Feed posts now render at their natural aspect ratio instead of being letterboxed into a uniform 4:3 frame. Tall posts read tall, wide posts read wide.",
    tag: "design",
  },
  {
    date: "2026-05-15",
    title: "Badges page expanded",
    body: "Every collectible badge is now visible — earned ones render in full, unearned ones show as locked placeholders with a 'how to earn' hint so you can see the whole set.",
    tag: "feature",
  },
  {
    date: "2026-05-15",
    title: "Visitor view privacy",
    body: "Tapping someone else's item on their profile or feed now correctly routes to the public summary view — pricing recommendations and the full forensic report stay owner-only.",
    tag: "fix",
  },
  {
    date: "2026-05-15",
    title: "Dating-ladder reference doc",
    body: "Brand entries in our authentication reference now lead with explicit era-marker ladders. The model commits to an era from the markers it sees rather than anchoring on the first chronological entry it reads.",
    tag: "accuracy",
  },
];

function humanDate(iso: string): string {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// Theme-safe tag styling. The previous palette (--marigold / --sand /
// --paper-3) didn't contrast reliably across themes — Trapper Keeper
// remaps --marigold to deep teal and --ink to deep berry, both dark;
// Velvet Underground remaps --marigold to pale lavender and --ink to
// pearl white, both light. Only --ink+--ink-fg and --rust+--rust-fg
// are guaranteed to contrast in every theme.
//
// Two variants:
//   rust-filled : prominent / positive updates  (feature, accuracy, brands)
//   ink-outline : infrastructure / housekeeping (design, fix)
type TagKind = "rust-filled" | "ink-outline";
const TAG_STYLE: Record<NonNullable<Entry["tag"]>, { kind: TagKind; label: string }> = {
  feature:  { kind: "rust-filled", label: "New" },
  accuracy: { kind: "rust-filled", label: "Accuracy" },
  brands:   { kind: "rust-filled", label: "Brands" },
  design:   { kind: "ink-outline", label: "Design" },
  fix:      { kind: "ink-outline", label: "Fix" },
};

function tagPillStyle(kind: TagKind): React.CSSProperties {
  if (kind === "rust-filled") {
    return {
      background: "hsl(var(--rust))",
      color: "hsl(var(--rust-fg))",
      border: "none",
    };
  }
  return {
    background: "transparent",
    color: "hsl(var(--ink))",
    border: "1px solid hsl(var(--ink))",
  };
}

export default function WhatsNew() {
  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
            aria-label="Back"
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">What's new</span>
          <div style={{ width: 30 }} />
        </div>

        <Section pad={14} eyebrow="Changelog">
          <div style={{
            background: "hsl(var(--ink))",
            color: "hsl(var(--cream))",
            padding: "20px 22px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
              fontSize: 30, lineHeight: 1.05, letterSpacing: "-0.02em",
            }}>
              Recent updates to FIG.
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontStyle: "italic",
              fontSize: 14, color: "hsl(var(--cream))", opacity: 0.85,
              marginTop: 8, lineHeight: 1.4,
            }}>
              New brands, accuracy tuning, design refinements, and fixes —
              most recent first.
            </div>
          </div>
        </Section>

        <Section pad={14}>
          {ENTRIES.map((e, i) => {
            const tag = e.tag ? TAG_STYLE[e.tag] : null;
            return (
              <Card key={`${e.date}-${i}`} style={{ padding: 14, marginBottom: 10 }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 8, gap: 8,
                }}>
                  <div className="caps" style={{
                    color: "hsl(var(--ink-2))", fontSize: 10,
                  }}>{humanDate(e.date)}</div>
                  {tag && (
                    <span style={{
                      ...tagPillStyle(tag.kind),
                      padding: "3px 8px", borderRadius: 3,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                      fontWeight: 700,
                    }}>{tag.label}</span>
                  )}
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 700, fontSize: 16, color: "hsl(var(--ink))",
                  lineHeight: 1.25, marginBottom: 6,
                }}>{e.title}</div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5,
                }}>{e.body}</div>
              </Card>
            );
          })}
        </Section>
      </div>
    </MobileFrame>
  );
}
